import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  analysis,
  policyholderFile,
  type CreditAnalysis,
  type LegalReview,
  type UnderwritingReview,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatDate,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { MotivoPadronizado } from '../shared/MotivoPadronizado';
import {
  sugerirCamposDoCredito,
  type CamposSugeridosDoCredito,
} from './sugestaoDoCredito';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * Pareceres da operacao: credito, subscricao e juridico.
 *
 * Cada quadro aparece apenas quando o servidor devolveu aquele parecer. Um quadro
 * ausente significa que a pessoa nao tem a permissao da area, e nao que o parecer
 * nao exista: quem esconde e o servidor, e a tela apenas reflete.
 *
 * Nenhuma decisao move a esteira. O avanco continua sendo a transicao explicita da
 * operacao, no quadro de proximo passo.
 */
/**
 * Etapas em que cada parecer ainda muda, as mesmas da trava do servidor
 * (`analysis/app/stage_lock.go`). Fora delas o painel so mostra o registro: os
 * botoes apareciam em qualquer etapa e o servidor respondia 409 (Credito, subscricao
 * e juridico 7 do 47, anexo C; P-EST-1).
 */
const etapasDoParecer = {
  credito: ['aguardando_credito'],
  subscricao: ['aguardando_credito', 'em_subscricao'],
  juridico: ['aguardando_credito', 'em_subscricao', 'em_juridico'],
} as const;

function parecerAberto(parecer: keyof typeof etapasDoParecer, stage?: string): boolean {
  if (!stage) return true;
  return (etapasDoParecer[parecer] as readonly string[]).includes(stage);
}

/**
 * De onde a analise de credito da operacao tira a sugestao: o tomador, o grupo de
 * credito da modalidade e a taxa aplicada na operacao.
 */
export interface SugestaoDoCredito {
  policyholderId?: string | undefined;
  grupo?: string | undefined;
  taxaBps?: number | undefined;
}

export function AnalysisPanels({
  operationId,
  stage,
  sugestao,
}: {
  operationId: string;
  /** Etapa da operacao. Sem ela, os botoes seguem so a permissao. */
  stage?: string | undefined;
  /** Sem ela, a analise de credito abre so com o que esta gravado. */
  sugestao?: SugestaoDoCredito | undefined;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const consulta = useQuery({
    queryKey: ['analysis', operationId],
    queryFn: ({ signal }) => analysis.get(operationId, signal),
    enabled: operationId !== '',
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['analysis', operationId] });
    void queryClient.invalidateQueries({ queryKey: ['operations', operationId] });
  }

  if (consulta.isPending) {
    return (
      <Card>
        <CardBody>
          <SkeletonText lines={5} />
        </CardBody>
      </Card>
    );
  }

  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar os pareceres">
        {consulta.error.message}
      </Alert>
    );
  }

  const { credit, underwriting, legal } = consulta.data;

  if (!credit && !underwriting && !legal) {
    return (
      <Alert tone="info" title="Pareceres indisponiveis para o seu acesso">
        O seu perfil nao inclui credito, subscricao nem juridico. As decisoes destas
        areas continuam visiveis na linha do tempo.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {credit ? (
        <div id="parecer-credito" className="scroll-mt-24">
          <CreditCard
            operationId={operationId}
            credit={credit}
            canEdit={can('credito.analise.update') && parecerAberto('credito', stage)}
            canDecide={
              can('credito.analise.approve') && parecerAberto('credito', stage)
            }
            locked={!parecerAberto('credito', stage)}
            sugestao={sugestao}
            onChanged={invalidar}
          />
        </div>
      ) : null}

      {underwriting ? (
        <div id="parecer-subscricao" className="scroll-mt-24">
          <UnderwritingCard
            operationId={operationId}
            review={underwriting}
            canEdit={
              can('subscricao.analise.update') && parecerAberto('subscricao', stage)
            }
            canDecide={
              can('subscricao.analise.approve') && parecerAberto('subscricao', stage)
            }
            locked={!parecerAberto('subscricao', stage)}
            onChanged={invalidar}
          />
        </div>
      ) : null}

      {legal ? (
        <div id="parecer-juridico" className="scroll-mt-24">
          <LegalCard
            operationId={operationId}
            review={legal}
            canEdit={can('juridico.parecer.update') && parecerAberto('juridico', stage)}
            canApprove={
              can('juridico.parecer.approve') && parecerAberto('juridico', stage)
            }
            locked={!parecerAberto('juridico', stage)}
            onChanged={invalidar}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Justificativa da aprovacao (C-ALC). A politica dos pareceres da empresa pode exigir
 * justificativa quando a aprovacao contraria a recomendacao do motor; o servidor
 * confere e responde com o campo que falta. Acima da alcada, a aprovacao e recusada e
 * o caso fica encaminhado ao nivel acima.
 */
function JustificativaDaAprovacao({
  area,
  value,
  onChange,
}: {
  area: 'credito' | 'subscricao';
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Textarea
      label={
        area === 'credito'
          ? 'Justificativa da aprovacao do credito'
          : 'Justificativa da aprovacao da subscricao'
      }
      value={value}
      hint="Exigida quando a politica da empresa pede justificativa para aprovar contra a recomendacao do motor. Fica no historico do parecer."
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/** Aviso do parecer cuja etapa ja ficou para tras. */
function ParecerTravado() {
  return (
    <p className="text-2xs text-ink-500">
      A operacao ja passou desta etapa: o parecer nao muda mais e fica como registro do
      que sustentou o avanco.
    </p>
  );
}

// --- Credito ---------------------------------------------------------------

const ORIGEM_DO_LIMITE = 'Sugerido: limite aprovado do tomador em Limites e Taxas.';
const ORIGEM_DA_TAXA = 'Sugerido: taxa aplicada na operacao.';

function CreditCard({
  operationId,
  credit,
  canEdit,
  canDecide,
  locked = false,
  sugestao,
  onChanged,
}: {
  operationId: string;
  credit: CreditAnalysis;
  canEdit: boolean;
  canDecide: boolean;
  locked?: boolean;
  sugestao?: SugestaoDoCredito | undefined;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [tradicional, setTradicional] = useState(
    moneyToInput(credit.traditional.limit),
  );
  const [judicial, setJudicial] = useState(moneyToInput(credit.judicial.limit));
  // A taxa de cada grupo passa a ter campo (Credito, subscricao e juridico 3 do 47,
  // anexo C): antes a tela sempre reenviava a gravada.
  const [taxaTradicional, setTaxaTradicional] = useState(
    rateToInput(credit.traditional.rate_bps),
  );
  const [taxaJudicial, setTaxaJudicial] = useState(
    rateToInput(credit.judicial.rate_bps),
  );
  const [validade, setValidade] = useState(credit.valid_until?.slice(0, 10) ?? '');
  const [parecer, setParecer] = useState(credit.opinion);
  const [motivo, setMotivo] = useState('');
  const [justificativa, setJustificativa] = useState('');

  // A sugestao so vale para a analise que ainda nao tem nada gravado nem decidido.
  const semValores =
    credit.status !== 'aprovado' &&
    credit.traditional.limit.amount === 0 &&
    credit.judicial.limit.amount === 0 &&
    credit.traditional.rate_bps === 0 &&
    credit.judicial.rate_bps === 0;
  const policyholderId = sugestao?.policyholderId ?? '';
  const limitesDoTomador = useQuery({
    queryKey: ['policyholder', policyholderId, 'credit-limits'],
    queryFn: ({ signal }) => policyholderFile.creditLimits(policyholderId, signal),
    enabled: canEdit && semValores && policyholderId !== '',
  });
  const [sugeridos, setSugeridos] = useState<CamposSugeridosDoCredito | null>(null);
  if (sugeridos === null && semValores && canEdit && limitesDoTomador.data) {
    const campos = sugerirCamposDoCredito(
      limitesDoTomador.data,
      sugestao?.grupo,
      sugestao?.taxaBps,
    );
    setSugeridos(campos);
    if (campos.tradicional && tradicional === moneyToInput(credit.traditional.limit)) {
      setTradicional(campos.tradicional);
    }
    if (campos.judicial && judicial === moneyToInput(credit.judicial.limit)) {
      setJudicial(campos.judicial);
    }
    if (
      campos.taxaTradicional &&
      taxaTradicional === rateToInput(credit.traditional.rate_bps)
    ) {
      setTaxaTradicional(campos.taxaTradicional);
    }
    if (campos.taxaJudicial && taxaJudicial === rateToInput(credit.judicial.rate_bps)) {
      setTaxaJudicial(campos.taxaJudicial);
    }
  }
  const origem = (valor: string, sugerido: string | undefined, texto: string) =>
    sugerido !== undefined && valor === sugerido ? ` ${texto}` : '';

  // O que esta na tela e ainda nao foi gravado. Aprovar com isso pendente aprovava o
  // que estava gravado, e gravar depois reabria a analise aprovada.
  const alterado =
    tradicional !== moneyToInput(credit.traditional.limit) ||
    judicial !== moneyToInput(credit.judicial.limit) ||
    taxaTradicional !== rateToInput(credit.traditional.rate_bps) ||
    taxaJudicial !== rateToInput(credit.judicial.rate_bps) ||
    validade !== (credit.valid_until?.slice(0, 10) ?? '') ||
    parecer !== credit.opinion;

  const gravar = () =>
    analysis.saveCredit(operationId, {
      traditional: {
        limit: toMoney(tradicional || '0'),
        rate_bps: rateToBasisPoints(taxaTradicional || '0'),
      },
      judicial: {
        limit: toMoney(judicial || '0'),
        rate_bps: rateToBasisPoints(taxaJudicial || '0'),
      },
      valid_until: validade || null,
      opinion: parecer,
    });

  const salvar = useMutation({
    mutationFn: gravar,
    onSuccess: () => {
      toast.success('Analise de credito salva.');
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const decidir = useMutation({
    mutationFn: async (status: 'aprovado' | 'reprovado' | 'pendente') => {
      // Aprovar grava antes o que esta na tela: a aprovacao vale para o limite e a
      // taxa que a pessoa ve, e nao para os de antes.
      if (status === 'aprovado' && canEdit && alterado) await gravar();
      return status === 'aprovado' && justificativa.trim() !== ''
        ? analysis.decideCredit(operationId, status, motivo, justificativa)
        : analysis.decideCredit(operationId, status, motivo);
    },
    onSuccess: (atualizado) => {
      toast.success(
        `Credito ${atualizado.status === 'aprovado' ? 'aprovado' : atualizado.status}.`,
      );
      setMotivo('');
      setJustificativa('');
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const erroTradicional = moneyInputError(tradicional);
  const erroJudicial = moneyInputError(judicial);
  const erroTaxaTradicional = rateInputError(taxaTradicional);
  const erroTaxaJudicial = rateInputError(taxaJudicial);
  const limiteInvalido =
    erroTradicional !== undefined ||
    erroJudicial !== undefined ||
    erroTaxaTradicional !== undefined ||
    erroTaxaJudicial !== undefined;

  return (
    <Card>
      <CardHeader
        title="Analise de credito"
        description="Limites por grupo de modalidade. O limite tradicional e o judicial nao se compensam."
        action={<StatusBadge kind="credito" value={credit.status} />}
      />
      <CardBody className="flex flex-col gap-4">
        {credit.expired ? (
          <Alert tone="warning" title="Limite com validade vencida">
            A validade do limite passou em {formatCivilDate(credit.valid_until)}. Uma
            nova analise e necessaria antes de liberar a operacao.
          </Alert>
        ) : null}

        {credit.status === 'reprovado' && credit.decline_reason ? (
          <Alert tone="danger" title="Credito reprovado">
            {credit.decline_reason}
          </Alert>
        ) : null}

        <fieldset
          disabled={!canEdit || salvar.isPending}
          className="grid gap-4 sm:grid-cols-3"
        >
          <MoneyField
            label="Limite tradicional (R$)"
            value={tradicional}
            error={erroTradicional ?? fieldErrorOf(apiError, 'traditional_limit')}
            hint={`Utilizado: ${formatMoney(credit.traditional.used)}. Disponivel: ${formatMoney(credit.traditional.available)}.${origem(tradicional, sugeridos?.tradicional, ORIGEM_DO_LIMITE)}`}
            onValueChange={setTradicional}
          />
          <MoneyField
            label="Limite judicial e financeiro (R$)"
            value={judicial}
            error={erroJudicial ?? fieldErrorOf(apiError, 'judicial_limit')}
            hint={`Utilizado: ${formatMoney(credit.judicial.used)}. Disponivel: ${formatMoney(credit.judicial.available)}.${origem(judicial, sugeridos?.judicial, ORIGEM_DO_LIMITE)}`}
            onValueChange={setJudicial}
          />
          <Field
            label="Validade do limite"
            type="date"
            value={validade}
            error={fieldErrorOf(apiError, 'valid_until')}
            onChange={(event) => setValidade(event.target.value)}
          />
          <Field
            label="Taxa tradicional (%)"
            inputMode="decimal"
            value={taxaTradicional}
            error={
              erroTaxaTradicional ?? fieldErrorOf(apiError, 'traditional_rate_bps')
            }
            hint={origem(
              taxaTradicional,
              sugeridos?.taxaTradicional,
              ORIGEM_DA_TAXA,
            ).trim()}
            onChange={(event) => setTaxaTradicional(event.target.value)}
          />
          <Field
            label="Taxa judicial e financeira (%)"
            inputMode="decimal"
            value={taxaJudicial}
            error={erroTaxaJudicial ?? fieldErrorOf(apiError, 'judicial_rate_bps')}
            hint={origem(taxaJudicial, sugeridos?.taxaJudicial, ORIGEM_DA_TAXA).trim()}
            onChange={(event) => setTaxaJudicial(event.target.value)}
          />
        </fieldset>

        <fieldset disabled={!canEdit || salvar.isPending}>
          <Textarea
            label="Parecer de risco"
            value={parecer}
            error={fieldErrorOf(apiError, 'opinion')}
            onChange={(event) => setParecer(event.target.value)}
          />
        </fieldset>

        {credit.analyst_label ? (
          <p className="text-2xs text-ink-500">
            Decidido por {credit.analyst_label}
            {credit.decided_at ? ` em ${formatDate(credit.decided_at)}` : ''}. Taxa
            tradicional de {formatRate(credit.traditional.rate_bps)}.
          </p>
        ) : null}

        {canDecide ? (
          <MotivoPadronizado
            escopo="credito"
            label="Motivo da reprovacao do credito"
            value={motivo}
            hint="Exigido apenas para reprovar. Fica na trilha de auditoria."
            onChange={setMotivo}
          />
        ) : null}
        {canDecide ? (
          <JustificativaDaAprovacao
            area="credito"
            value={justificativa}
            onChange={setJustificativa}
          />
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
          {canEdit ? (
            <Button
              variant="secondary"
              loading={salvar.isPending}
              disabled={limiteInvalido}
              onClick={() => salvar.mutate()}
            >
              Salvar analise
            </Button>
          ) : null}
          {canDecide ? (
            <>
              <Button
                variant="secondary"
                disabled={decidir.isPending}
                onClick={() => decidir.mutate('pendente')}
              >
                Marcar credito pendente
              </Button>
              <Button
                variant="danger"
                disabled={decidir.isPending || motivo.trim() === ''}
                onClick={() => decidir.mutate('reprovado')}
              >
                Reprovar credito
              </Button>
              <Button
                loading={decidir.isPending}
                disabled={canEdit && alterado && limiteInvalido}
                onClick={() => decidir.mutate('aprovado')}
              >
                Aprovar credito
              </Button>
            </>
          ) : null}
        </div>

        {canDecide ? (
          <p className="text-2xs text-ink-500">
            A decisao registra o parecer e nao move a esteira. Aprovar grava antes o
            limite e a taxa da tela. O avanco continua no quadro de proximo passo.
          </p>
        ) : null}
        {locked ? <ParecerTravado /> : null}
      </CardBody>
    </Card>
  );
}

// --- Subscricao ------------------------------------------------------------

function UnderwritingCard({
  operationId,
  review,
  canEdit,
  canDecide,
  locked = false,
  onChanged,
}: {
  operationId: string;
  review: UnderwritingReview;
  canEdit: boolean;
  canDecide: boolean;
  locked?: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [notas, setNotas] = useState(review.notes);
  const [motivo, setMotivo] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [conferidos, setConferidos] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(review.checklist.map((item) => [item.id, item.checked])),
  );

  const pendentes = review.checklist.filter((item) => !conferidos[item.id]).length;
  // A aprovacao segue o roteiro gravado, e nao as caixas marcadas na tela: caixa
  // marcada e nao salva levava ao 422 do servidor (Credito, subscricao e juridico 5
  // do 47, anexo C).
  const pendentesGravados = review.checklist.filter((item) => !item.checked).length;
  const naoSalvo = review.checklist.some(
    (item) => Boolean(conferidos[item.id]) !== item.checked,
  );

  const salvar = useMutation({
    mutationFn: () =>
      analysis.saveUnderwriting(operationId, {
        notes: notas,
        checklist: review.checklist.map((item) => ({
          id: item.id,
          checked: Boolean(conferidos[item.id]),
        })),
      }),
    onSuccess: () => {
      toast.success('Subscricao salva.');
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const decidir = useMutation({
    mutationFn: (status: 'aprovada' | 'reprovada') =>
      status === 'aprovada' && justificativa.trim() !== ''
        ? analysis.decideUnderwriting(operationId, status, motivo, justificativa)
        : analysis.decideUnderwriting(operationId, status, motivo),
    onSuccess: (atualizado) => {
      toast.success(`Subscricao ${atualizado.status}.`);
      setMotivo('');
      setJustificativa('');
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card>
      <CardHeader
        title="Subscricao"
        description="O roteiro precisa estar inteiro conferido para aprovar."
        action={<StatusBadge kind="subscricao" value={review.status} />}
      />
      <CardBody className="flex flex-col gap-4">
        {review.status === 'reprovada' && review.decline_reason ? (
          <Alert tone="danger" title="Subscricao reprovada">
            {review.decline_reason}
          </Alert>
        ) : null}

        {review.reviewer_label ? (
          <p className="text-2xs text-ink-500">
            Decidido por {review.reviewer_label}
            {review.decided_at ? ` em ${formatDate(review.decided_at)}` : ''}.
          </p>
        ) : null}

        <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
          {review.checklist.map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-3 py-2.5">
              <input
                id={`checklist-${item.id}`}
                type="checkbox"
                disabled={!canEdit}
                checked={Boolean(conferidos[item.id])}
                onChange={(event) =>
                  setConferidos((atual) => ({
                    ...atual,
                    [item.id]: event.target.checked,
                  }))
                }
                className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
              />
              <label
                htmlFor={`checklist-${item.id}`}
                className="min-w-0 flex-1 cursor-pointer text-sm text-ink-700"
              >
                {item.label}
              </label>
            </li>
          ))}
        </ul>

        <fieldset disabled={!canEdit || salvar.isPending}>
          <Textarea
            label="Observacoes da subscricao"
            value={notas}
            onChange={(event) => setNotas(event.target.value)}
          />
        </fieldset>

        {canDecide ? (
          <MotivoPadronizado
            escopo="subscricao"
            label="Motivo da reprovacao da subscricao"
            value={motivo}
            hint="Exigido apenas para reprovar."
            onChange={setMotivo}
          />
        ) : null}
        {canDecide ? (
          <JustificativaDaAprovacao
            area="subscricao"
            value={justificativa}
            onChange={setJustificativa}
          />
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4">
          {pendentes > 0 ? (
            <span className="mr-auto text-2xs text-ink-500">
              {pendentes} {pendentes === 1 ? 'item por conferir' : 'itens por conferir'}
            </span>
          ) : naoSalvo ? (
            <span className="mr-auto text-2xs text-ink-500">
              Salve o roteiro antes de aprovar.
            </span>
          ) : null}

          {canEdit ? (
            <Button
              variant="secondary"
              loading={salvar.isPending}
              onClick={() => salvar.mutate()}
            >
              Salvar
            </Button>
          ) : null}
          {canDecide ? (
            <>
              <Button
                variant="danger"
                disabled={decidir.isPending || motivo.trim() === ''}
                onClick={() => decidir.mutate('reprovada')}
              >
                Reprovar subscricao
              </Button>
              <Button
                loading={decidir.isPending}
                disabled={pendentesGravados > 0 || naoSalvo}
                onClick={() => decidir.mutate('aprovada')}
              >
                Aprovar subscricao
              </Button>
            </>
          ) : null}
        </div>
        {locked ? <ParecerTravado /> : null}
      </CardBody>
    </Card>
  );
}

// --- Juridico --------------------------------------------------------------

function LegalCard({
  operationId,
  review,
  canEdit,
  canApprove,
  locked = false,
  onChanged,
}: {
  operationId: string;
  review: LegalReview;
  canEdit: boolean;
  canApprove: boolean;
  locked?: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [parecer, setParecer] = useState(review.opinion);

  const decidir = useMutation({
    mutationFn: (status: 'em_analise' | 'pendencia' | 'aprovado') =>
      analysis.decideLegal(operationId, status, parecer),
    onSuccess: () => {
      toast.success('Parecer juridico registrado.');
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card>
      <CardHeader
        title="Parecer juridico"
        description="A pendencia devolve a operacao para ajuste, e nao a encerra."
        action={<StatusBadge kind="juridico" value={review.status} />}
      />
      <CardBody className="flex flex-col gap-4">
        <fieldset disabled={(!canEdit && !canApprove) || decidir.isPending}>
          <Textarea
            label="Parecer"
            value={parecer}
            hint="Exigido para aprovar ou registrar pendencia."
            onChange={(event) => setParecer(event.target.value)}
          />
        </fieldset>

        {review.reviewer_label ? (
          <p className="text-2xs text-ink-500">
            Registrado por {review.reviewer_label}
            {review.decided_at ? ` em ${formatDate(review.decided_at)}` : ''}.
          </p>
        ) : null}

        {/* Aprovar pede so juridico.parecer.approve, como no servidor: o diretor, que
            aprova sem editar, nao via o botao (Credito, subscricao e juridico 4 do
            47, anexo C). */}
        {canEdit || canApprove ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
            {canEdit ? (
              <>
                <Button
                  variant="secondary"
                  disabled={decidir.isPending}
                  onClick={() => decidir.mutate('em_analise')}
                >
                  Colocar em analise
                </Button>
                <Button
                  variant="secondary"
                  disabled={decidir.isPending || parecer.trim() === ''}
                  onClick={() => decidir.mutate('pendencia')}
                >
                  Registrar pendencia juridica
                </Button>
              </>
            ) : null}
            {canApprove ? (
              <Button
                loading={decidir.isPending}
                disabled={parecer.trim() === ''}
                onClick={() => decidir.mutate('aprovado')}
              >
                Aprovar parecer
              </Button>
            ) : null}
          </div>
        ) : null}
        {locked ? <ParecerTravado /> : null}
      </CardBody>
    </Card>
  );
}
